import { ButtonSecondary, Colors, Icon, Pills, Text } from '@amelio/ui-library';

import type { CompensationEnvelopeStatus } from '@/types/compensation';

import { useAmelioTranslations } from '@/hooks';

interface EnvelopeActionBarProps {
    status: CompensationEnvelopeStatus;
    onOpenHistory: () => void;
    onToggleStatus: () => void;
    isTogglingStatus: boolean;
}

/** Status of the envelope and the two actions that act on the envelope as a whole: its change
 * history, and closing or reopening it. A closed envelope is read-only for everyone, so the bar
 * also carries the sentence that says so. It takes whatever width the action bar leaves beside the
 * save pair, and keeps its actions at the end of it. */
export const EnvelopeActionBar = ({
    status,
    onOpenHistory,
    onToggleStatus,
    isTogglingStatus,
}: EnvelopeActionBarProps) => {
    const { t } = useAmelioTranslations();

    const isClosed = status === 'Closed';

    return (
        <div className={'flex flex-1 flex-wrap items-center justify-end gap-[12px]'}>
            {isClosed ? (
                <Text
                    color={Colors.BASE_500}
                    className={'mr-auto'}
                >
                    {t('compensation.actionBar.closedNotice')}
                </Text>
            ) : null}

            <Pills
                variant={isClosed ? 'inactiveTinted' : 'draftTinted'}
                size={'tinted'}
                dataTestId={'compensation-envelope-status'}
            >
                {t(isClosed ? 'compensation.status.closed' : 'compensation.status.draft')}
            </Pills>

            <ButtonSecondary
                type={'button'}
                size={'sm'}
                onClick={onOpenHistory}
                leftIcon={
                    <Icon
                        name={'time-clock'}
                        size={'sm'}
                        ariaHidden
                    />
                }
                dataTestId={'compensation-history-button'}
            >
                {t('compensation.actionBar.history')}
            </ButtonSecondary>

            <ButtonSecondary
                type={'button'}
                size={'sm'}
                onClick={onToggleStatus}
                loading={isTogglingStatus}
                disabled={isTogglingStatus}
                dataTestId={'compensation-toggle-status-button'}
            >
                {t(isClosed ? 'compensation.actionBar.reopen' : 'compensation.actionBar.close')}
            </ButtonSecondary>
        </div>
    );
};
